import {LinkNavigationDescriptor} from '@/lib/types/components/navigation';
import {ImageMedia} from '@/lib/types/media';
import React, {useEffect} from 'react';
import {NavbarLogo} from './NavbarLogo';

type NavbarProps = Readonly<{
  children: React.ReactNode;
  collapseBelow?: 'sm' | 'md' | 'lg' | 'xl';
  logoLink?: LinkNavigationDescriptor | string;
  mobileLogo?: ImageMedia;
  desktopLogo?: ImageMedia;
}>;

/**
 * Renders a standard Navbar that is capable of collapsing into a mobile-friendly view
 * @param children - The children to render within the navbar
 */
export function Navbar(props: NavbarProps) {
  useEffect(() => {
    const detailsElements = Array.from(document.querySelectorAll('details'));
    document.addEventListener('click', e => {
      if (
        !detailsElements.some(
          f => e.target instanceof Node && f.contains(e.target)
        )
      ) {
        detailsElements.forEach(f => f.removeAttribute('open'));
      }
    });
    detailsElements.forEach(deet => {
      deet.addEventListener('toggle', toggleOpenOneOnly);
    });
    function toggleOpenOneOnly() {
      if (this.open) {
        detailsElements.forEach(deet => {
          if (deet !== this && deet.open) deet.open = false;
        });
      }
    }

    // close all menus if focus moves to outside the navigation
    const main = document.querySelector('main');
    const login = document.querySelector('.login-nav');
    main?.addEventListener('focusin', event => {
      detailsElements.forEach(deet => {
        deet.open = false;
      });
    });
    login?.addEventListener('focusin', event => {
      detailsElements.forEach(deet => {
        deet.open = false;
      });
    });

    // Function to check the viewport width and add/remove attributes accordingly
    const html = document.querySelector('html');
    const navHeader = document.querySelector('nav.header');
    const offscreenElement =
      document.querySelector<HTMLButtonElement>('#offscreen-nav');
    const offscreenClose = document.querySelector<HTMLButtonElement>(
      '#offscreen-nav .btn-close'
    );
    const offscreenToggler = document.querySelector('.offscreen-toggler');
    const inertElements = document.querySelectorAll(
      'main, footer.site-footer, .site-banner, #skip, .department-logo, .nav-search, .offscreen-toggler'
    );
    const localNav = document.querySelector('.local-nav > ul > li');

    if (localNav) {
      if (offscreenToggler) {
        // Create ResizeObserver for local-nav

        // Check if the offscreen element popover is open using :popover-open pseudo-class
        offscreenElement.addEventListener('toggle', () => {
          if (offscreenElement.matches(':popover-open')) {
            offscreenClose?.focus();
            inertElements.forEach(element => {
              element.setAttribute('inert', 'true');
            });
          } else {
            inertElements.forEach(element => {
              element.removeAttribute('inert');
            });
          }
        });

        const onResize = () => {
          setOffscreenState(html, offscreenElement, navHeader, inertElements);
        };
        window.addEventListener('resize', onResize);

        // Call the function once to set the initial state
        setOffscreenState(html, offscreenElement, navHeader, inertElements);
        checkHasNavOverflow(
          offscreenElement,
          navHeader,
          inertElements,
          offscreenToggler,
          offscreenClose
        );
      }
    }
  });

  return (
    <nav className="header z-3">
      <NavbarLogo
        mobileLogo={props.mobileLogo}
        desktopLogo={props.desktopLogo}
        link={props.logoLink}
      />
      <ToggleNavbarButton />
      <div className="offscreen" id="offscreen-nav" popover="auto">
        <OffCanvasNavigation />
        {props.children}
      </div>
    </nav>
  );
}

/**
 * Creates a button that toggles the mobile navbar utilizing Bootstrap functionality
 */
function ToggleNavbarButton() {
  return (
    <button
      className="btn btn-icon offscreen-toggler"
      title="Toggle navigation"
      {...{popovertarget: 'offscreen-nav'}}
      {...{popovertargetaction: 'show'}}
    >
      {' '}
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
        {' '}
        <path d="M0 96C0 78.3 14.3 64 32 64l384 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 128C14.3 128 0 113.7 0 96zM0 256c0-17.7 14.3-32 32-32l384 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 288c-17.7 0-32-14.3-32-32zM448 416c0 17.7-14.3 32-32 32L32 448c-17.7 0-32-14.3-32-32s14.3-32 32-32l384 0c17.7 0 32 14.3 32 32z"></path>
      </svg>{' '}
    </button>
  );
}

/**
 * Creates a button to close the mobile navbar utilizing Bootstrap functionality
 */
function OffCanvasNavigation() {
  return (
    <button
      className="btn btn-close btn-close-white"
      aria-label="Close"
      {...{popovertarget: 'offscreen-nav'}}
      {...{popovertargetaction: 'hide'}}
    ></button>
  );
}

function checkHasNavOverflow(
  offscreenElement,
  navHeader,
  inertElements,
  offscreenToggler,
  offscreenClose
) {
  // let navHasOverflow;
  const navItemsArray = [];
  navItemsArray.length = 0;
  const localNavItems = document.querySelectorAll('.local-nav > ul > li');
  localNavItems.forEach(navItem => {
    navItemsArray.push(navItem.getBoundingClientRect().width);
  });

  const totalNavItemsWidth = navItemsArray.reduce(
    (accumulator, currentValue) => {
      return accumulator + currentValue;
    }
  );

  const localNavObserver = new ResizeObserver(entries => {
    entries.forEach(entry => {
      const localNavWidth = entry.contentRect.width;
      if (totalNavItemsWidth > localNavWidth || totalNavItemsWidth === 0) {
        // return navHasOverflow;
        offscreenElement.setAttribute('popover', 'auto');
        navHeader.classList.remove('full');
        offscreenToggler?.classList.remove('invisible');
        offscreenClose?.classList.remove('invisible');
      } else {
        offscreenElement.removeAttribute('popover');
        navHeader.classList.add('full');
        offscreenToggler?.classList.add('invisible');
        offscreenClose?.classList.add('invisible');

        // Remove the 'inert' attribute regardless of popover state (since full nav will show)
        inertElements.forEach(element => {
          element.removeAttribute('inert');
        });
      }
    });
  });

  // Attach ResizeObserver
  const localNav = document.querySelector('.local-nav');
  localNavObserver.observe(localNav);
}

function setOffscreenState(html, offscreenElement, navHeader, inertElements) {
  if (offscreenElement) {
    if (html.clientWidth < 992) {
      // Set the menu to offscreen state
      offscreenElement.setAttribute('popover', 'auto');
      navHeader.classList.remove('full');
    } else {
      // Set the menu to full state (not offscreen)
      offscreenElement.removeAttribute('popover');
      navHeader.classList.add('full');

      // Remove the 'inert' attribute regardless of popover state (since viewport is larger)
      inertElements.forEach(element => {
        element.removeAttribute('inert');
      });
    }
  }
}
